import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

// SF and MUI Components
import MuiDrawer from "@mui/material/Drawer";
import MuiList from "@mui/material/List";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import SFIconButton from "../button/iconButton";
import SFTypography from "../typography";
import { SFSwapHorizIcon, SFLogoutIcon } from "../icons";

// Hooks and Utilities
import getStyled from "../../hooks/getStyled";
import { hasPermission } from "../authorized";
import { getMenuState } from "./helpers";

// Components
import { AhaIdeaPlugin } from "../aha";
import EyLogo from "../ey-logo";
import { SFStack } from "../grid";
// Menu Collection components
import MenuItems from "./menuItems";
import UserNameMenuItem from "./menuItems/userNameMenuItem";
import FooterItem from "./menuItems/footerItem";
import VersionLink from "./menuItems/versionLink";

// Dialogs and Confirmations
import TenantChangeConfirmation from "./tenantChangeConfirmation";
import LogoutConfirmation from "./logoutConfirmation";
import { Logger, baseContainer } from "@base-provider";


const { styled } = getStyled();

const ChangeTenantLinkContainer = styled(SFStack)(({ theme }) => ({
  // Default style overrides for the container
  paddingLeft: 24,
  paddingRight: 24,
  paddingTop: 4,
  paddingBottom: 10,
  "& .MuiIconButton-root": {
    // Default style overrides for the icon button
    width: 20,
    height: 20,
    "& .MuiSvgIcon-root": {
      // Default style overrides for the icon
      width: 20,
      height: 20,
      color: theme.palette.grey400.main,
      border: `0.5px solid ${theme.palette.white.main}`,
      borderRadius: 4,
    },
    "&:hover": {
      // Style overrides for the icon button when hovered
      "& .MuiSvgIcon-root": {
        // Style overrides for the icon when the icon button is hovered
        color: theme.palette.grey450.main,
        borderColor: theme.palette.grey200.main,
        backgroundColor: theme.palette.grey100.main,
      },
    },
  },
}));

const drawerWidth = 110;

const StyledDrawer = styled(MuiDrawer)(({ theme }) => ({
  width: drawerWidth,
  height: "100vh",
  flexShrink: 0,
  boxSizing: "border-box",
  "& .MuiPaper-root": {
    boxShadow: `4px 0px 6px 0px ${theme.palette.grey200.main}`,
  },
  "& .MuiDrawer-paper": {
    width: drawerWidth,
  }
}));

const DrawerHeader = styled(SFStack)(() => ({
  paddingTop: 10,
}));

const MenuContainer = styled(MuiList)(() => ({
  paddingTop: 0,
  flexGrow: 1,
  overflowY: "auto",
  overflowX: "hidden",
}));

const MenuContainerBottom = styled(MuiList)(({ theme }) => ({
  width: "100%",
  position: "sticky",
  bottom: 0,
  paddingTop: 0,
  paddingBottom: 0,
  maxWidth: drawerWidth,
  borderTop: `1px solid ${theme.palette.grey225.main}`,
  backgroundColor: theme.palette.white.main,
}));

const SFSideMenu = (props) => {
  const [userTenants, setUserTenants] = useState([]);
  const { t } = useTranslation();
  const [isChangeTenantModalOpen, setIsChangeTenantModalOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const location = useLocation();
  const selectedRoute = location.pathname;
  const [menuState, setMenuState] = useState(getMenuState(selectedRoute));
  const tenantConfig = useSelector((state) => state.session.tenantSettings);
  const forceTenant = useMemo(() => baseContainer.getConfig().forceTenant, []);

  useEffect(() => {
    if (forceTenant) {
      baseContainer.authenticationService.getTenants().then((res) => {
        setUserTenants(res);
      });
    }
  }, []);

  const filteredRoutes = useMemo(
    () =>
      props.routes
        .filter((a) => hasPermission(a.permissions))
        .map((x) =>
          !x.children
            ? x
            : {
              ...x,
              children: x.children.filter((y) =>
                hasPermission(y.permissions)
              ),
            }
        ),
    [props.routes]
  );

  const leftsideRoutes = useMemo(() => {
    return props.routes.filter(
      (route) => route.showInSideMenu && route.path.split("/").length <= 2 && hasPermission(route.permissions)
    );
  }, [props.routes]);

  const releaseNotesRoute = useMemo(
    () => filteredRoutes?.find((r) => r.location === "release-notes"),
    [filteredRoutes]
  );

  const shouldRenderChangeTenantLink =
    forceTenant && userTenants.filter((x) => x.isActive).length > 1;

  const toggleRoute = (route) => {
    Logger.of("App.Core.SideMenu.toggleRoute").info({ route, menuState });
    setMenuState((previousState) => {
      return { ...previousState, [route.path]: !previousState[route.path] };
    });
  };

  return (
    <>
      <StyledDrawer variant="permanent">
        <DrawerHeader
          alignItems="center"
          justifyContent={forceTenant ? "flex-end" : "center"}
          spacing={1}
        >
          <EyLogo
            style={{
              width: 24,
              height: 27,
              marginBottom: shouldRenderChangeTenantLink ? 0 : 20,
            }}
          />
          {/* 
              If there is more than 1 tenant, render the ChangeTenantLinkContainer (Change Tenant Icon and Tenant Name)
          */}
          {shouldRenderChangeTenantLink && (
            <ChangeTenantLinkContainer
              alignItems="center"
              justifyContent="center"
              spacing={0.5}
            >
              <SFIconButton
                onClick={() => setIsChangeTenantModalOpen((prev) => !prev)}
                title={t("navigation.switchTenant")}
                ariaLabel={t("navigation.switchTenant")}
              >
                <SFSwapHorizIcon />
              </SFIconButton>
              <SFTypography
                color="grey500"
                variant="leftNav"
                noWrap={false}
                align="center">
                {tenantConfig?.tenantName ?? t("generic.blank")}
              </SFTypography>
            </ChangeTenantLinkContainer>
          )}
        </DrawerHeader>
        <MenuContainer component="nav" >
          {/* Help, Benchmark, Content Studio menu items */}
          <MenuItems
            selectedRoute={selectedRoute}
            filteredRoutes={leftsideRoutes}
            toggleRoute={toggleRoute}
            menuState={menuState}
          />
        </MenuContainer>
        <MenuContainerBottom component="div" >
          {/* UserName Icon and Username text component */}
          <UserNameMenuItem username={props.username} />
          <MenuItems
            selectedRoute={selectedRoute}
            filteredRoutes={filteredRoutes.filter(
              (r) => r.location == "footer"
            )}
            toggleRoute={toggleRoute}
            menuState={menuState}
            isFooter={true}
          />
          {/* Feedback menu component */}
          {props.env?.REACT_APP_AHA_API_KEY && (
            <AhaIdeaPlugin
              username={props.email}
              apiKey={props.env.REACT_APP_AHA_API_KEY}
              projectCode={props.env.REACT_APP_AHA_PROJECT_CODE}
              RenderItem={({ onClick }) => (
                <FooterItem
                  label={t("navigation.submitIdea")}
                  onClick={onClick}
                  icon={<LightbulbOutlinedIcon fontSize="sizebarNavigation" />}
                ></FooterItem>
              )}
            />
          )}
          {/* Logout FooterItem */}
          <FooterItem
            sx={{ paddingBottom: "7.5px" }}
            label={t("navigation.logout")}
            onClick={() => setConfirmLogout(true)}
            icon={<SFLogoutIcon fontSize="sizebarNavigation" />}
          />
          {props.activeVersion && releaseNotesRoute && (
            <VersionLink
              activeVersion={props.activeVersion}
              releaseNotesRoute={releaseNotesRoute}
            />
          )}
        </MenuContainerBottom>
      </StyledDrawer>
      <TenantChangeConfirmation
        onClose={() => setIsChangeTenantModalOpen(false)}
        open={isChangeTenantModalOpen}
      />
      {confirmLogout && (
        <LogoutConfirmation onClose={() => setConfirmLogout(false)} />
      )}
    </>
  );
};

SFSideMenu.propTypes = {
  /**
   * The routes to display in the side menu.
   */
  routes: PropTypes.any,
  /**
   * The username of the current user.
   */
  username: PropTypes.string.isRequired,
  /**
   * The active version of the application.
   */
  activeVersion: PropTypes.string,
  /**
   * The environment variables.
   */
  env: PropTypes.object,
  /**
   * Property used for Aha menu item (if it's rendered).
   */
  email: PropTypes.string,
};

export default SFSideMenu;
